import { useState, useEffect } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import type { FieldSource } from "../../../models/workPaperTemplate";

export interface StaticFieldFormData {
  sheetName: string;
  cellRow: number;
  cellColumn: string;
  source: FieldSource;
  sourceSelector: string;
}

interface StaticFieldFormProps {
  value: StaticFieldFormData;
  onChange: (value: StaticFieldFormData) => void;
  readOnly?: boolean;
  // Which sources the template's type allows; narrows the Source Type
  // dropdown so a field can't be created against an incompatible source.
  allowedSources: FieldSource[];
}

// Mapping of source types to their available fields
// Add new source types here as they become available
const FIELD_OPTIONS: Record<
  FieldSource,
  Array<{ value: string; label: string }>
> = {
  control: [
    { value: "name", label: "Control Name" },
    { value: "description", label: "Control Description" },
    { value: "cycle", label: "Cycle" },
    { value: "process", label: "Process" },
    { value: "controlOwner", label: "Control Owner" },
    { value: "attributes", label: "Control Attributes" },
  ],
  work_step: [
    { value: "name", label: "Work Step Name" },
    { value: "description", label: "Description" },
    { value: "section", label: "Section" },
    { value: "subsection", label: "Subsection" },
    { value: "preparer", label: "Preparer" },
    { value: "ops_audit_name", label: "Audit" },
    { value: "attributes", label: "Work Step Attributes" },
  ],
  // Future: Add other source types here
  // procedure: [...],
  // attribute: [...],
};

// Source type options - currently "control" and "work_step" are supported
const SOURCE_OPTIONS: Array<{ value: FieldSource; label: string }> = [
  { value: "control", label: "Control" },
  { value: "work_step", label: "Work Step" },
  // Future: Add other source types here
  // { value: "procedure", label: "Procedure" },
  // { value: "attribute", label: "Attribute" },
];

export function StaticFieldForm({
  value,
  onChange,
  readOnly = false,
  allowedSources,
}: StaticFieldFormProps) {
  const [formData, setFormData] = useState<StaticFieldFormData>(value);

  useEffect(() => {
    setFormData(value);
  }, [value]);

  const handleChange = (
    field: keyof StaticFieldFormData,
    newValue: string | number,
  ) => {
    const updated = { ...formData, [field]: newValue };
    setFormData(updated);
    onChange(updated);
  };

  // Get available fields based on selected source
  const availableFields = FIELD_OPTIONS[formData.source] || [];

  // Get label for the field selector based on source type
  const getFieldLabel = () => {
    switch (formData.source) {
      case "control":
        return "Control Field";
      case "work_step":
        return "Work Step Field";
      default:
        return "Field";
    }
  };

  // Get helper text for field selector based on source type
  const getFieldHelperText = () => {
    switch (formData.source) {
      case "control":
        return "This field will be populated with data from the selected control property";
      case "work_step":
        return "This field will be populated with data from the selected work step property";
      default:
        return "This field will be populated with data from the selected property";
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="sheetName">Sheet Name</Label>
          <Input
            id="sheetName"
            value={formData.sheetName}
            onChange={(e) => handleChange("sheetName", e.target.value)}
            placeholder="Sheet1"
            disabled={readOnly}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="cellRow">Row Number</Label>
          <Input
            id="cellRow"
            type="number"
            min={1}
            value={formData.cellRow}
            onChange={(e) =>
              handleChange("cellRow", parseInt(e.target.value, 10) || 1)
            }
            placeholder="1"
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cellColumn">Column Letter</Label>
        <Input
          id="cellColumn"
          value={formData.cellColumn}
          onChange={(e) =>
            handleChange("cellColumn", e.target.value.toUpperCase())
          }
          placeholder="A"
          disabled={readOnly}
          className="uppercase"
        />
        <p className="text-sm text-muted-foreground">
          Use Excel column letters (A, B, C, ... Z, AA, AB, etc.)
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="source">Source Type</Label>
        <Select
          value={formData.source}
          onValueChange={(newValue) => handleChange("source", newValue)}
          disabled={readOnly}
        >
          <SelectTrigger id="source">
            <SelectValue placeholder="Select a source type" />
          </SelectTrigger>
          <SelectContent>
            {SOURCE_OPTIONS.filter((option) =>
              allowedSources.includes(option.value),
            ).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          The context from which to pull data for this field
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sourceSelector">{getFieldLabel()}</Label>
        <Select
          value={formData.sourceSelector}
          onValueChange={(newValue) => handleChange("sourceSelector", newValue)}
          disabled={readOnly}
        >
          <SelectTrigger id="sourceSelector">
            <SelectValue
              placeholder={`Select a ${getFieldLabel().toLowerCase()}`}
            />
          </SelectTrigger>
          <SelectContent>
            {availableFields.map((field) => (
              <SelectItem key={field.value} value={field.value}>
                {field.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">{getFieldHelperText()}</p>
      </div>

      <div className="rounded-md bg-muted p-3 text-sm">
        <strong>Preview:</strong> {formData.sheetName}!{formData.cellColumn}
        {formData.cellRow}
      </div>
    </div>
  );
}
